/**
 * Контракты четвёрки (BE-222, MVP_TECH_RFC §21).
 *
 * Зеркало `backend/app/modules/selection/schemas.py`. Генерации типов из
 * OpenAPI в проекте пока нет (S-15), поэтому контракт описан вручную и обязан
 * совпадать буквально — расхождение ловит E2E на живом стеке.
 *
 * Три правила, которые нельзя «упростить»:
 *
 * * **`null` — это «нет данных», а не ноль.** `match = null` означает, что
 *   снимок результата прогона не найден, а не что соответствие нулевое;
 * * **backend не присылает текстов.** Обоснование отбора, направление
 *   сравнения и значения справочников приходят кодами; строки собирает i18n;
 * * **лимитов в коде фронта нет.** Размер четвёрки, предел замен и число
 *   проверенных автомобилей приходят с сервера: литералов в интерфейсе быть
 *   не может (DESIGN_SYSTEM §8).
 */

import type { Conclusion, ConclusionKind, Recommendation } from '@entities/conclusion/types';
import type {
  Drivetrain,
  FuelType,
  MarketComparisonData,
  Transmission,
  VehicleCoverImage,
  VehicleLocation,
  VehiclePrice,
} from '@entities/vehicle/types';

/** Состояние строки четвёрки (MVP_TECH_RFC §7.5.1). */
export type SelectionStatus = 'active' | 'selected' | 'rejected_by_user' | 'dropped';

/**
 * Почему автомобиль покинул слот (§34.13).
 *
 * Причины — факты, а не суждения о продавце: пользователь видит «объявление
 * снято» и «автохаус не ответил», а не «вариант недоступен» (MVP_PRD §34.5).
 */
export type SelectionExitReason =
  | 'USER_REJECTED'
  /**
   * Отказ **после** углублённой проверки (§34.5). Отдельный код от
   * `USER_REJECTED` не ради аналитики: он означает, что состав заморожен и
   * замены не будет — слот остаётся пустым, а четвёрка становится тройкой.
   */
  | 'USER_REJECTED_AFTER_CHECK'
  | 'VEHICLE_UNAVAILABLE'
  | 'SELLER_CONTACT_MISSING'
  | 'SELLER_CONTACT_SUPPRESSED'
  | 'DEALER_NO_RESPONSE'
  | 'DEALER_DECLINED'
  | 'USER_CHOSE_ANOTHER'
  | 'CRITERIA_CHANGED';

/** Необязательная причина отказа со слов пользователя (MVP_PRD §34.5). */
export const REJECT_REASONS = [
  'too_expensive',
  'condition',
  'dealer_unresponsive',
  'too_far',
  'equipment',
  'other',
] as const;

export type RejectReason = (typeof REJECT_REASONS)[number];

/** Персональное соответствие. Зеркало `MatchOut` (BE-087). */
export interface SelectionMatch {
  score: number;
  category: string;
  match_confidence: number;
  match_confidence_category: string;
  data_confidence: number;
  /** Доля применённой модели, 0–100 (MATCHING §8.3). */
  scoring_coverage: number;
  top_reasons: { code: string; params?: Record<string, unknown>; evidence?: unknown[] }[];
}

/** Рыночный блок карточки — снимок на момент прогона. */
export interface SelectionMarket {
  status: string;
  match_level: string | null;
  median_price_cents: number | null;
  delta_cents: number | null;
  delta_percent: number | null;
  sample_size: number | null;
  snapshot_date: string | null;
  is_stale: boolean;
}

/**
 * Карточка автомобиля четвёрки — всегда полная.
 *
 * Закрытого варианта у неё нет и быть не может: четвёрка существует только
 * внутри оплаченного заказа (§34.11).
 */
export interface SelectionVehicle {
  vehicle_id: string;
  slug: string;
  title: string;
  make: string;
  model: string;
  variant: string | null;
  body_type: string;
  price: VehiclePrice;
  first_registration_date: string;
  mileage_km: number;
  fuel_type: FuelType;
  transmission: Transmission;
  drivetrain: Drivetrain | null;
  power_kw: number | null;
  location: VehicleLocation;
  cover_image: VehicleCoverImage | null;
  /** Продавец — информационный блок. Рейтинга и оценки надёжности нет (§37.1). */
  seller_name: string | null;
  seller_city: string | null;
  /** `null` — снимок результата не найден: «Нет данных», а не ноль. */
  match: SelectionMatch | null;
  market: SelectionMarket | null;
  attention_count: number | null;
  /** `false` — объявление снято, Karl подбирает замену (§34.13). */
  is_published: boolean;
}

/**
 * Обоснование отбора — снимок на момент попадания в четвёрку (§34.3).
 *
 * Не пересчитывается: данные автомобиля меняются, а обоснование обязано
 * соответствовать тому, что видел пользователь.
 */
export interface SelectionBasis {
  rank?: number;
  total_score?: number;
  match_category?: string;
  scoring_coverage?: number;
  strengths?: { code: string; params?: Record<string, unknown>; evidence?: unknown[] }[];
  compromise?: { code: string; params?: Record<string, unknown>; evidence?: unknown[] } | null;
  /** Действовало ли ограничение разнообразия на этом шаге. */
  diversity_rule_applied?: boolean;
  /** `home` / `neighbour` / `rest`; `null` — домашняя земля не названа. */
  geo_step?: string | null;
  preferred_make?: boolean;
  /** `GEO_STEP` / `PREFERRED_MAKE` / `RANK`; `null` — ничьей не было. */
  tie_break_reason?: string | null;
  selection_version?: string;
}

/**
 * Компонент второй оценки с основанием (§38.5).
 *
 * `applicable = false` — компонент **исключён из нормализации, а не обнулён**:
 * «не о чем судить» и «проверили, не подтвердилось» — разные утверждения.
 * У такого компонента `weight` и `value` пусты.
 */
export interface PostCheckComponent {
  /** `match_fit` | `answer_coverage` | … — переводится через i18n. */
  code: string;
  /** Базовый вес из таблицы §38.5, до нормализации. */
  base_weight: number;
  applicable: boolean;
  /** Доля в нормализованной сумме, %. `null` — компонент неприменим. */
  weight: number | null;
  /** Балл компонента 0–100. `null` — компонент неприменим. */
  value: number | null;
  /** Идентификаторы ответов, цитаты и коды расхождений — основание балла. */
  evidence: Record<string, unknown>[];
}

/**
 * Оценка после проверки — **вторая и отдельная** (MVP_PRD §38.5).
 *
 * Отвечает на вопрос «насколько подтверждено то, что мы знаем об этом
 * автомобиле», и только на него. Рядом всегда стоит Match Score, и
 * **складывать их нельзя**: общего «итогового балла» в продукте нет.
 *
 * Формулировка на экране — «Сведения подтверждены на 78 из 100», а не
 * «автомобиль на 78 из 100». Слова «надёжный», «безопасный», «в хорошем
 * состоянии» рядом с числом запрещены.
 */
export interface PostCheckScore {
  score: number;
  algorithm_version: string;
  calculated_at: string;
  components: PostCheckComponent[];
}

export interface SelectionSlot {
  selection_id: string;
  slot: number;
  sequence_number: number;
  status: SelectionStatus;
  entered_at: string;
  exited_at: string | null;
  exit_reason: SelectionExitReason | null;
  replaced_by_selection_id: string | null;
  selection_version: string;
  basis: SelectionBasis;
  vehicle: SelectionVehicle;
  /**
   * Вторая оценка. `null` — не считалась: «Нет данных», а не ноль.
   *
   * Лежит **рядом** с `vehicle.match`, а не внутри него: два числа отвечают на
   * разные вопросы и никогда не складываются.
   */
  post_check: PostCheckScore | null;
  /**
   * Когда Karl закончил вопросы по этому автомобилю (§36.6). `null` —
   * проверка идёт. Пока она идёт, выбрать автомобиль нельзя — правило
   * сервисного слоя (`SELECTION_CHECK_PENDING`); поле лишь объясняет экрану,
   * почему кнопки ещё нет.
   */
  check_completed_at: string | null;
  /**
   * Переписка по автомобилю в этом заказе (§34.4). Выбор автомобиля и торг
   * живут на её карточке — «Выбрать этот автомобиль» ведёт туда. `null` —
   * автохаусу ещё не писали.
   */
  negotiation_id: string | null;
}

/** Состояние согласия: какая редакция ожидается и принята ли она. */
export interface ConsentState {
  required: boolean;
  /** Хэш редакции текста. Клиент возвращает его же — доказательство редакции. */
  text_hash: string;
  accepted_at: string | null;
}

export interface SelectionState {
  order_id: string;
  stage: string;
  selection_version: string;
  size: number;
  max_size: number;
  /** Слот освободился, замену подбирает фоновая задача. */
  top_up_pending: boolean;
  checked_vehicle_count: number;
  max_checked_vehicles: number;
  replacement_count: number;
  max_replacements: number;
  /** Сколько автомобилей рабочего набора ещё не участвовали в заказе. */
  candidates_left: number;
  /** Кандидаты и лимиты исчерпаны — штатный исход, а не ошибка (§34.6). */
  exhausted: boolean;
  /**
   * Состав закрыт подтверждением четвёрки (§34.5).
   *
   * Экран говорит **почему** замен больше нет, а не показывает неактивную
   * кнопку без объяснения: «Не подходит» здесь заменяется на «Отказаться от
   * этого автомобиля», и это другое действие с другим последствием.
   */
  frozen: boolean;
  /** Когда состав закрыт. `null` — заморозки ещё не было. */
  frozen_at: string | null;
  /**
   * **Первый** выбранный автомобиль. Оставлен ради уже написанного кода
   * воронки; актуальный состав выбора — `selected_vehicle_ids`.
   */
  selected_vehicle_id: string | null;
  /**
   * Выбранный автомобиль (§34.4). Ноль или один элемент: выбор один на заказ
   * и окончателен. Состояние карточки читается из `slot.status`.
   */
  selected_vehicle_ids: string[];
  outreach: ConsentState;
  fee_consent: ConsentState;
  /** Собственных вопросов пользователя в мандате (§36.3). Число с сервера. */
  max_custom_questions: number;
  slots: SelectionSlot[];
  /** Вся история заказа: экран исчерпания разбирает каждый проверенный вариант. */
  history: SelectionSlot[];
}

// --- Сравнение ---------------------------------------------------------------------

/** Куда «лучше» в строке. `none` — сравнивать нечего (§34.5). */
export type ComparisonDirection = 'lower' | 'higher' | 'none';

/** Как форматировать значение. `code` переводится i18n, `text` — данные как есть. */
export type ComparisonUnit =
  | 'year'
  | 'kilometres'
  | 'kilowatt'
  | 'currency'
  | 'litres'
  | 'kwh'
  | 'percent'
  | 'score'
  | 'count'
  /** Рабочие минуты DE (§34.8): ночь и выходные в них не входят. */
  | 'minutes'
  | 'code'
  | 'text';

export interface ComparisonValue {
  numeric: number | null;
  code: string | null;
  text: string | null;
}

export interface ComparisonRowData {
  code: string;
  unit: ComparisonUnit;
  direction: ComparisonDirection;
  values: ComparisonValue[];
  /** Пусто — лучшего нет: нет направления, все значения равны или пусты. */
  best_indexes: number[];
}

export interface ComparisonGroupData {
  /**
   * `vehicle` | `dealer_answers` | `answer_coverage` | `dealer_facts` (§34.5).
   *
   * Группы **не сводятся в общий балл**: «какой автомобиль», «как отвечал
   * автохаус» и «что известно об автохаусе» — три разных вопроса, и число, в
   * котором они смешаны, было бы запрещённым агрегатом-вердиктом (§37.1).
   */
  code: string;
  rows: ComparisonRowData[];
}

export interface ComparisonColumn {
  vehicle_id: string;
  slot: number;
  make: string;
  model: string;
  variant: string | null;
  /**
   * Вторая оценка — полем колонки, а не строкой таблицы (§38.5).
   *
   * Строкой она встала бы рядом со строкой Match Score, и подсветка «лучшего»
   * смешала бы два разных вопроса в один рейтинг. `null` до `deep_check_ready`.
   */
  post_check: PostCheckScore | null;
}

export interface ComparisonState {
  order_id: string;
  stage: string;
  columns: ComparisonColumn[];
  groups: ComparisonGroupData[];
  /**
   * Рекомендация Karl (BE-251).
   *
   * `null` — два разных состояния, и склеивать их нельзя: **не рекомендован ни
   * один** (тогда заполнен `no_recommendation_reason`) либо **заключений ещё
   * нет** (тогда пуст `conclusions`, и блок рекомендации не рисуется —
   * сравнение полезно само по себе).
   */
  recommendation: Recommendation | null;
  /**
   * Вид заключений: `preliminary` до углублённой проверки, `post_check` после
   * (§38.5). `null` — заключений ещё нет.
   */
  conclusion_kind: ConclusionKind | null;
  /** Почему не рекомендован ни один вариант — **штатный исход**, не ошибка. */
  no_recommendation_reason: string | null;
  /** Базовые веса ранжирования: модалка «Как рассчитано». Числа с сервера. */
  weights: Record<string, number>;
  /** Заключение по каждому автомобилю заказа, включая выбывшие (§38.2). */
  conclusions: Conclusion[];
}

/**
 * Рыночный блок карточки в форме, которую понимают общие помощники каталога.
 *
 * Медиана приходит центами, а помощники ждут цену: валюта у медианы та же,
 * что у объявления, — выборка сравнивается в одной валюте (FE-595; раньше
 * медиана здесь терялась, и блок в четвёрке не мог её показать).
 */
export function toMarketComparison(
  market: SelectionMarket | null,
  currency: string,
): MarketComparisonData | null {
  if (market === null) return null;
  return {
    status: market.status as MarketComparisonData['status'],
    match_level: market.match_level as MarketComparisonData['match_level'],
    median_price:
      market.median_price_cents === null
        ? null
        : { amount_cents: market.median_price_cents, currency, formatted: '' },
    delta_cents: market.delta_cents,
    delta_percent: market.delta_percent,
    sample_size: market.sample_size,
    snapshot_date: market.snapshot_date,
    is_stale: market.is_stale,
    confidence_score: 0,
    calculated_at: '',
  };
}
